import type { AvatarMode } from '../../types/SharedAvatarProps';

export const getAvatarInitials = (label?: string) => {
  const trimmed = label?.trim() ?? '';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return null;
  }

  if (parts.length === 1) {
    const [word] = parts;
    const initial = word[0]?.toUpperCase() ?? '';
    return initial.length > 0 ? initial : null;
  }

  const initials = `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  return initials.length > 0 ? initials : null;
};

export const getAvatarAbbreviatedName = (name?: string) => {
  const trimmed = name?.trim() ?? '';
  const parts = trimmed.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return undefined;
  }

  if (parts.length === 1) {
    return parts[0];
  }

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase();

  if (!lastInitial) {
    return firstName;
  }

  return `${firstName} ${lastInitial}.`;
};

type AvatarTextState = {
  inGroup: boolean;
  mode?: AvatarMode;
  name?: string;
};

const showsInlineText = (mode?: AvatarMode) =>
  mode === 'abbreviated' || mode === 'info' || mode === 'name';

export const getAvatarInlineText = ({
  inGroup,
  mode,
  name,
  text,
}: AvatarTextState & { text?: string }) => {
  const resolvedName = (text ?? name)?.trim();
  if (inGroup || !showsInlineText(mode) || !resolvedName) {
    return undefined;
  }

  if (mode === 'abbreviated') {
    return getAvatarAbbreviatedName(resolvedName);
  }

  return resolvedName;
};

export const getAvatarChipTooltip = ({
  name,
  tooltip,
}: Pick<AvatarTextState, 'name'> & { tooltip?: string }) => {
  const resolvedName = name?.trim();
  const resolvedTooltip = tooltip?.trim();

  if (resolvedName) {
    return resolvedTooltip ?? resolvedName;
  }

  return resolvedTooltip;
};

export const getAvatarInlineTooltip = ({
  mode,
  name,
  text,
  tooltip,
}: Pick<AvatarTextState, 'mode' | 'name'> & {
  text?: string;
  tooltip?: string;
}) => {
  const resolvedName = (text ?? name)?.trim();
  const resolvedTooltip = tooltip?.trim();

  if (resolvedTooltip) {
    return resolvedTooltip;
  }

  if (mode === 'abbreviated' && resolvedName) {
    return resolvedName;
  }

  return undefined;
};
